// "Add photos" on a shared logbook trip -- the one write a visitor can make.
//
// It is mounted on BOTH shared surfaces from one component: the entries page
// passes `entries` and gets a day picker, the entry page passes `entryId` and gets
// none. Photos are stored per entry (`.../logbook/<entryId>/<file>`), so a day is
// not optional -- there is no "the trip" to attach one to.
//
// THE DAY PICKER HAS NO PRESELECTION, deliberately. Defaulting to the first or the
// newest day would silently misfile someone's photos, and a visitor has no way to
// notice or undo it. "Choose a day..." costs one tap and is the difference between
// a feature and a mess in the owner's logbook.
//
// NOT StorageManager, which every other uploader in this app uses: that needs
// Amplify Storage credentials an anonymous visitor does not have. A plain
// <input type="file"> plus presigned PUTs is the whole mechanism -- see
// src/utilities/shareUpload.mjs.
//
// `accept` mirrors canPreview's set, which also gets iOS to convert a HEIC to
// JPEG on pick rather than handing us a file no browser can decode. The same rule
// is then re-checked in JS (so the message is ours, not the file dialog's silence)
// and again server-side.
//
// FAILURES ARE INLINE AND PER FILE. Someone adding eight photos on marina wifi
// must not lose seven to one timeout, and "three added, one was a HEIC" is the
// honest report. Nothing here reaches the page's status: this is a card, not the
// page's data.

import { useState, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { DataCard } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { uploadSharedPhotos } from '../../utilities/shareUpload.mjs';
import { entryHeading } from '../../fleet-shared/LogbookTypes.mjs';
import {
  MAX_GUEST_UPLOAD_FILES,
  MAX_GUEST_UPLOAD_FILE_SIZE,
} from '../../fleet-shared/Attachments.mjs';

// The formats the endpoint will sign for. Spelled as MIME types here because that
// is what a file dialog filters on; the rule itself is guestUploadRejection's.
const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';

const MB = (bytes) => Math.round(bytes / (1024 * 1024));

export function SharedPhotoUpload({ shareId, entryId, entries, typeId, placeIndex, onAdded }) {
  // Only rendered with a day already chosen, or with days to choose from.
  const needsDay = !entryId;
  const [chosenDay, setChosenDay] = useState('');
  const [name, setName] = useState('');
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const targetEntry = entryId || chosenDay;
  const busy = progress !== null;

  const add = async (files) => {
    setError(null);
    setResult(null);

    if (!targetEntry) {
      setError('Choose which day these photos belong to first.');
      return;
    }

    setProgress({ done: 0, total: files.length });
    try {
      const outcome = await uploadSharedPhotos({
        shareId,
        entryId: targetEntry,
        files,
        uploaderName: name.trim(),
        onProgress: setProgress,
      });
      setResult(outcome);
      if (outcome.photos.length > 0 && onAdded) onAdded(targetEntry, outcome.photos);
    } catch (err) {
      setError(err);
    } finally {
      setProgress(null);
      // So picking the same file again re-fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <DataCard
      header={<CardHeader text="Add Photos" />}
      body={
        <>
          <p className="text-body-secondary small">
            Were you on this trip? Add your own photos. They&apos;ll show on this page
            for anyone with the link, and the trip&apos;s owner will be emailed and can
            remove any of them. Up to {MAX_GUEST_UPLOAD_FILES} at a time,{' '}
            {MB(MAX_GUEST_UPLOAD_FILE_SIZE)} MB each &mdash; JPEG, PNG, WebP or AVIF.
          </p>

          {needsDay && (
            <Form.Group className="mb-3" controlId="shared-upload-day">
              <Form.Label className="small">Which day?</Form.Label>
              <Form.Select
                value={chosenDay}
                disabled={busy}
                onChange={(e) => setChosenDay(e.target.value)}
              >
                {/* No preselection: see the header. */}
                <option value="">Choose a day...</option>
                {/* entryHeading already leads with the date (see LogbookTypes:
                    "entryHeading prefixes the date"), so adding displayDate here
                    would print it twice. */}
                {(entries ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entryHeading(entry, typeId, { placeIndex })}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="shared-upload-name">
            <Form.Label className="small">Your name (optional)</Form.Label>
            <Form.Control
              type="text"
              value={name}
              disabled={busy}
              maxLength={80}
              placeholder="So the owner knows who sent them"
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="shared-upload-files">
            <Form.Label className="small">Photos</Form.Label>
            <Form.Control
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              disabled={busy || (needsDay && !chosenDay)}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) add(files);
              }}
            />
          </Form.Group>

          {busy && (
            <div className="mt-3">
              <p className="small text-body-secondary mb-1">
                Uploading {progress.done} of {progress.total}...
              </p>
              <ProgressBar
                style={{ height: 4 }}
                now={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
            </div>
          )}

          {error && (
            <div className="alert alert-warning py-2 small mt-3 mb-0" role="alert">
              {error.gone
                ? 'This shared trip is no longer available.'
                : error.message || String(error)}
            </div>
          )}

          {result && <UploadOutcome result={result} />}

          {!busy && !error && !result && needsDay && !chosenDay && (
            <p className="small text-body-secondary mt-2 mb-0">
              Choose a day to add photos to.
            </p>
          )}
        </>
      }
    />
  );
}

// What actually happened, per file where it differs. A batch is routinely partly
// good, and "4 photos added" while one silently vanished is the version people
// stop trusting.
function UploadOutcome({ result }) {
  const added = result.photos.length;
  const trouble = [...result.rejected, ...result.failed];

  return (
    <div className="mt-3 small">
      {added > 0 && (
        <p className="text-success mb-1">
          {added === 1 ? '1 photo added' : `${added} photos added`}
          {result.notified ? ", and the owner has been emailed." : '.'}
        </p>
      )}
      {trouble.length > 0 && (
        <ul className="text-body-secondary mb-0 ps-3">
          {trouble.map((item) => (
            <li key={`${item.name}-${item.reason}`}>
              <strong>{item.name}</strong>: {item.reason}
            </li>
          ))}
        </ul>
      )}
      {added === 0 && trouble.length === 0 && (
        <p className="text-body-secondary mb-0">Nothing was added.</p>
      )}
    </div>
  );
}

export default SharedPhotoUpload;
